import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useAuth } from '@/features/auth';
import type { AdminUserUpdateRequest, CreateUserRequest, UserProfile } from '../model/types';
import {
  createUser,
  deleteUser,
  getAllUsers,
  getUserById,
  searchUsers,
  setUserPassword,
  setUserRoles,
  updateUser,
} from './userApi';

/** Od ilu znakow pytamy backend — krotsze frazy zwracalyby pol bazy. */
const MIN_SEARCH_LENGTH = 2;

/**
 * Klucze pamieci podrecznej. Trzymamy je w jednym miejscu, zeby uniewaznianie
 * danych po mutacjach nie opieralo sie na przepisywanych recznie napisach.
 */
export const userKeys = {
  all: ['users'] as const,
  detail: (id: string) => [...userKeys.all, 'detail', id] as const,
  search: (query: string) => [...userKeys.all, 'search', query] as const,
  adminList: () => [...userKeys.all, 'admin-list'] as const,
};

/**
 * Wyszukuje graczy po poczatku imienia lub nazwiska.
 *
 * Fraza krotsza niz {@link MIN_SEARCH_LENGTH} znakow nie odpala zapytania —
 * `GET /api/users/search` nie ma stronicowania i zwrocilby maksymalna paczke.
 */
export function useUserSearchQuery(query: string): UseQueryResult<UserProfile[]> {
  const trimmed = query.trim();

  return useQuery({
    queryKey: userKeys.search(trimmed),
    queryFn: () => searchUsers(trimmed),
    enabled: trimmed.length >= MIN_SEARCH_LENGTH,
  });
}

/** Pobiera profil dowolnego uzytkownika. */
export function useUserQuery(id: string | undefined): UseQueryResult<UserProfile> {
  return useQuery({
    queryKey: userKeys.detail(id ?? ''),
    queryFn: () => getUserById(id ?? ''),
    enabled: Boolean(id),
  });
}

/**
 * Pobiera profil zalogowanego uzytkownika.
 *
 * Identyfikator bierzemy z tokena — backend nie ma endpointu `/api/users/me`,
 * wiec pytamy o konkretny zasob po UUID z claimu `sub`.
 */
export function useCurrentUserQuery(): UseQueryResult<UserProfile> {
  const { user } = useAuth();
  return useUserQuery(user?.id);
}

/** Lista wszystkich uzytkownikow — panel ADMIN. */
export function useAdminUsersQuery(enabled: boolean): UseQueryResult<UserProfile[]> {
  return useQuery({
    queryKey: userKeys.adminList(),
    queryFn: getAllUsers,
    enabled,
  });
}

/** Uniewaznia liste panelu ADMIN i cache profili po kazdej mutacji na kontach. */
function useAdminUsersInvalidation() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: userKeys.all });
  };
}

/** Tworzy konto uzytkownika z panelu ADMIN. */
export function useCreateUserMutation(): UseMutationResult<UserProfile, Error, CreateUserRequest> {
  const invalidate = useAdminUsersInvalidation();
  return useMutation({ mutationFn: createUser, onSuccess: invalidate });
}

/** Edytuje imie, nazwisko i status konta (ADMIN). */
export function useUpdateUserMutation(): UseMutationResult<
  UserProfile,
  Error,
  { id: string; request: AdminUserUpdateRequest }
> {
  const invalidate = useAdminUsersInvalidation();
  return useMutation({
    mutationFn: ({ id, request }) => updateUser(id, request),
    onSuccess: invalidate,
  });
}

/** Trwale usuwa konto uzytkownika (ADMIN). */
export function useDeleteUserMutation(): UseMutationResult<void, Error, string> {
  const invalidate = useAdminUsersInvalidation();
  return useMutation({ mutationFn: deleteUser, onSuccess: invalidate });
}

/** Nadaje uzytkownikowi nowe haslo (ADMIN). */
export function useSetUserPasswordMutation(): UseMutationResult<
  void,
  Error,
  { id: string; password: string }
> {
  return useMutation({
    mutationFn: ({ id, password }) => setUserPassword(id, password),
  });
}

/** Nadpisuje role globalne uzytkownika (ADMIN). */
export function useSetUserRolesMutation(): UseMutationResult<
  void,
  Error,
  { id: string; roles: string[] }
> {
  const invalidate = useAdminUsersInvalidation();
  return useMutation({
    mutationFn: ({ id, roles }) => setUserRoles(id, roles),
    onSuccess: invalidate,
  });
}
